import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STARTERS = [
  "What's on the dim sum menu?",
  "Tell me about Mahjong Linner Club",
  "How do tiles work?",
  "When does Dubai open?",
];

export default function ConciergeDialog({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `s-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "Welcome to Duck & Rice. Ask about bookings, the menu, or how Linner Club works — I'll keep it brief.",
      }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  if (!open) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/chat", { message: msg, session_id: sessionId });
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const err = formatApiError(e.response?.data?.detail) || "Concierge unavailable";
      setMessages(m => [...m, { role: "assistant", content: `(${err})` }]);
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dr-modal" onClick={onClose} data-testid="concierge-dialog">
      <div className="dr-concierge-card" onClick={(e) => e.stopPropagation()}>
        <div className="dr-concierge-head">
          <div>
            <div className="dr-modal-eyebrow">CONCIERGE</div>
            <h2 className="dr-modal-title" style={{ marginTop: 4 }}>The House Host</h2>
          </div>
          <button onClick={onClose} className="dr-close-x" data-testid="concierge-close">✕</button>
        </div>
        <div className="dr-modal-rule" />

        <div className="dr-chat-scroll" ref={scrollRef} data-testid="chat-scroll">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`dr-bubble ${m.role}`}
              data-testid={`bubble-${m.role}-${i}`}
            >
              {m.content}
            </div>
          ))}
          {busy && <div className="dr-bubble assistant typing">●●●</div>}
        </div>

        {messages.length <= 1 && (
          <div className="dr-starters">
            {STARTERS.map((s, i) => (
              <button
                key={i}
                className="dr-starter"
                onClick={() => send(s)}
                disabled={busy}
                data-testid={`starter-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          className="dr-chat-input-row"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <input
            data-testid="concierge-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the concierge…"
            disabled={busy}
          />
          <button
            type="submit"
            className="dr-chat-send"
            disabled={busy || !input.trim()}
            data-testid="concierge-send"
          >
            SEND
          </button>
        </form>
      </div>
    </div>
  );
}
